import { z } from 'zod';
import { defineCollection, type RecordOf } from '../collection.ts';
import { cents, currencyCode, id, isoDate, longText, requiredText, shortText } from '../fields.ts';

/**
 * Lightweight family goals and budgets. Amounts are entered by hand; there is
 * intentionally no bank connectivity, payment execution or financial advice.
 */
export const GOAL_KINDS = ['savings', 'budget', 'purchase', 'project'] as const;

export const goals = defineCollection({
  name: 'goals',
  module: 'goals',
  label: { one: 'goal', other: 'goals' },
  schema: z.object({
    title: requiredText(120),
    kind: z.enum(GOAL_KINDS).default('savings'),
    targetCents: cents.refine((v) => v >= 0, 'Must be zero or more'),
    currency: currencyCode.default('USD'),
    targetDate: isoDate.nullable().default(null),
    icon: shortText(16).default(''),
    notes: longText(5_000).default(''),
    completed: z.boolean().default(false),
  }),
  defaultAudience: 'adults',
  childAccess: 'read',
  editPolicy: 'collaborative',
  immutableFields: [],
  manualMergeFields: ['notes'],
});

/** A manual progress entry: money set aside (savings) or spent (budget). */
export const goalEntries = defineCollection({
  name: 'goal_entries',
  module: 'goals',
  label: { one: 'goal entry', other: 'goal entries' },
  schema: z.object({
    goalId: id,
    amountCents: cents,
    date: isoDate,
    note: shortText(500).default(''),
  }),
  parent: { kind: 'fixed', collection: 'goals', field: 'goalId' },
  defaultAudience: 'adults',
  childAccess: 'read',
  editPolicy: 'collaborative',
  immutableFields: ['goalId'],
  manualMergeFields: [],
});

export type Goal = RecordOf<typeof goals>;
export type GoalEntry = RecordOf<typeof goalEntries>;
